import { safelyJoinPaths } from '@trarc/pc-shared';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAppContainerContext } from '../../../contexts/AppContainerContext';
import { useSessionContext } from '../../../contexts/SessionContext';
import { useUpdatePageGroups } from '../../../hooks';
import { useCreateNavigationEventMutation } from '../../../types/api.graphql';
import { SideBarPageGroups } from '../../../types/enums';
import { HELP_TAB_ENTITIES } from '../constants';
import { HelpTabNavItem } from '../types';
import {
  NestedControlledCollapse,
  PageGroupProps,
  StyledButton,
  StyledCameraIcon,
  StyledControlledCollapse,
  StyledHelpIcon,
  StyledNavLink,
} from './GeneralPageGroup.styles';
import { TitleIconRow } from './TitleIconRow';

const HelpPageGroup = ({ isExpanded }: PageGroupProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const previousLocation = useRef<string | null>(null);
  const { setPreviousPath } = useSessionContext();
  const [createNavigationEvent] = useCreateNavigationEventMutation();
  const [openNestedPageGroups, setOpenNestedPageGroups] = useState(false);
  const { setShowContactModal } = useAppContainerContext();
  const { openPageGroups, updateOpenPageGroups } = useUpdatePageGroups();

  const isHelpCollapseOpen = isExpanded && openPageGroups.includes(SideBarPageGroups.Help);

  useEffect(() => {
    previousLocation.current = pathname;
  }, []);

  const logNavigationEvent = (navItem: HelpTabNavItem): void => {
    const toPath = t(HELP_TAB_ENTITIES.find((entity) => entity.value === navItem).path);

    setPreviousPath(toPath);

    void createNavigationEvent({
      variables: {
        input: {
          toPathname: toPath,
          fromPathname: previousLocation.current,
        },
      },
    });
    previousLocation.current = toPath;
  };

  return (
    <StyledControlledCollapse
      title={
        <TitleIconRow
          icon={<StyledHelpIcon />}
          title={t('help.title')}
          $isExpanded={isExpanded}
          open={isHelpCollapseOpen}
        />
      }
      onClick={() => updateOpenPageGroups(SideBarPageGroups.Help)}
      open={isHelpCollapseOpen}
      $isExpanded={isExpanded}
    >
      <StyledButton
        onClick={() => {
          setShowContactModal(true);
          logNavigationEvent(HelpTabNavItem.ContactUs);
        }}
      >
        {t('contactUs.label')}
      </StyledButton>
      <StyledNavLink
        to={safelyJoinPaths(process.env.REACT_APP_GRAPHQL_URI, 'api', 'help')}
        target="_blank"
        onClick={() => logNavigationEvent(HelpTabNavItem.Documentation)}
      >
        {t('help.documentation')}
      </StyledNavLink>
      <NestedControlledCollapse
        title={
          <TitleIconRow
            icon={<StyledCameraIcon />}
            title={t('help.videos')}
            $isExpanded={isExpanded}
            open={openNestedPageGroups}
          />
        }
        onClick={() => setOpenNestedPageGroups(!openNestedPageGroups)}
        open={openNestedPageGroups}
        $isExpanded={isExpanded}
        $isCollapseOpened={openNestedPageGroups}
      >
        <StyledNavLink
          to={
            'https://player.vimeo.com/video/906787629?h=292d8f108d&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479'
          }
          target="_blank"
          onClick={() => logNavigationEvent(HelpTabNavItem.GettingStartedVideo)}
        >
          {t('help.linkTitles.shorthandRoadmapTutorial')}
        </StyledNavLink>
        <StyledNavLink
          to={
            'https://player.vimeo.com/video/906787554?h=551e644fd1&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479'
          }
          target="_blank"
          onClick={() => logNavigationEvent(HelpTabNavItem.SafeguardsRuleReviewVideo)}
        >
          {t('help.linkTitles.shorthandFTCReview')}
        </StyledNavLink>
      </NestedControlledCollapse>
    </StyledControlledCollapse>
  );
};

export default HelpPageGroup;
